import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatIcon} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {FlexModule} from "@angular/flex-layout";
import {IndustryScopeType, SelectableIndustry} from "../industry.model";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatInput} from "@angular/material/input";
import {MatTooltip} from "@angular/material/tooltip";
import {ManageIndustriesService} from "../manage-industries.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";

@Component({
  selector: 'app-industry-item',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSlideToggle,
    MatHint,
    MatIcon,
    FlexModule,
    ReactiveFormsModule,
    MatError,
    MatTooltip,
    MatSuffix
  ],
  templateUrl: './industry-item.component.html',
  styleUrl: './industry-item.component.scss'
})
export class IndustryItemComponent implements OnInit {
  @Input() industry!: SelectableIndustry;
  @Output() industryChanged = new EventEmitter<SelectableIndustry>();

  isEditing = false;
  editForm!: FormGroup;

  constructor(private fb: FormBuilder,
              private industriesService: ManageIndustriesService,
              private loader: LoadingService,
              private snackBar: SnackbarService,
  ) {}

  ngOnInit() {
    this.initForm();
  }

  private initForm() {
    this.editForm = this.fb.group({
      labelName: ['', [Validators.maxLength(100)]],
      labelDescription: ['', [Validators.maxLength(500)]]
    });
  }

  get displayName(): string {
    // For master industries, show override if exists, otherwise original name
    if (this.industry.typeScope === IndustryScopeType.MASTER) {
      return this.industry.labelNameOverride || this.industry.name;
    }
    // For offering industries, always show override or original
    return this.industry.labelNameOverride || this.industry.name;
  }

  get displayDescription(): string {
    // For master industries, show override if exists, otherwise original description
    if (this.industry.typeScope === IndustryScopeType.MASTER) {
      return this.industry.labelDescriptionOverride || this.industry.description || '';
    }
    // For offering industries, always show override or original
    return this.industry.labelDescriptionOverride || this.industry.description || '';
  }

  get isMasterIndustry(): boolean {
    return this.industry.typeScope === IndustryScopeType.MASTER;
  }

  get canSave(): boolean {
    return this.editForm.valid && this.editForm.dirty;
  }

  get canCancel(): boolean {
    return this.isEditing;
  }

  onSelectionChange() {
    this.emitChange();
  }

  startEditing() {
    this.isEditing = true;
    if (this.isMasterIndustry) {
      this.editForm.patchValue({
        labelName: this.industry.labelNameOverride || this.industry.name,
        labelDescription: this.industry.labelDescriptionOverride || this.industry.description
      });
    } else {
      this.editForm.patchValue({
        labelName: this.industry.name,
        labelDescription: this.industry.description
      });
    }
    this.editForm.markAsPristine();
    this.editForm.markAsUntouched();
  }

  saveEditing() {
    if (!this.editForm.valid) {
      return;
    }

    const formValue = this.editForm.value;
    const trimmedName = formValue.labelName.trim();
    const trimmedDescription = formValue.labelDescription.trim();

    if (this.isMasterIndustry) {
      this.industry.labelNameOverride = trimmedName || this.industry.name;
      this.industry.labelDescriptionOverride = trimmedDescription || this.industry.description;
      this.isEditing = false;
      this.emitChange();
    } else {
      this.loader.show();

      const updatedIndustry = {
        ...this.industry,
        name: trimmedName || this.industry.name,
        description: trimmedDescription || this.industry.description
      };

      this.industriesService.saveOfferingIndustry(updatedIndustry, this.industry.industryCategoryId!).subscribe({
        next: (savedIndustry) => {
          this.industry.name = savedIndustry.name;
          this.industry.description = savedIndustry.description;
          this.isEditing = false;
          this.loader.hide();
        },
        error: (error) => {
          console.error('Error saving category:', error);
          this.snackBar.openSnackBar("An error occurred. Please try again later.");
          this.loader.hide();
        }
      });
    }
  }

  cancelEditing() {
    this.isEditing = false;
    this.editForm.reset();
  }

  resetLabelNameToDefault() {
    if (this.industry.name) {
      this.editForm.patchValue({ labelName: this.industry.name });
      this.editForm.markAsDirty();
    }
  }

  resetLabelDescriptionToDefault() {
    if (this.industry.description) {
      this.editForm.patchValue({ labelDescription: this.industry.description });
      this.editForm.markAsDirty();
    }
  }

  private emitChange() {
    this.industryChanged.emit({...this.industry});
  }
}
